import { get } from 'lodash';
import React from 'react';
import { useTranslation } from 'react-i18next';

import './Email.scss';
import { addTargetBlankToLinks } from './Email.utils';

/**
 * Used for wrapping email bodies into an iframe sandbox to isolate css styles
 * of the email from the rest of the application.
 */
const EmailContentIframe = (props: { content: string; isDarkTheme: boolean }) => {
  const { t } = useTranslation();
  const writeHTML = (frame: any) => {
    if (!frame) {
      return;
    }

    const doc = frame.contentDocument;
    doc.open();
    doc.write(props.content);

    // Inverts the color of the email when dark mode theme is selected
    if (props.isDarkTheme) {
      const invert =
        '-webkit-filter: invert(100%);' +
        '-moz-filter: invert(100%);' +
        '-o-filter: invert(100%);' +
        '-ms-filter: invert(100%);' +
        'filter: invert(100%);';

      const css = `
        html { ${invert} };
      `;

      const appendTo = doc.querySelector('head') || doc.querySelector('html') || doc;

      const style = doc.createElement('style');
      style.type = 'text/css';

      // Invert images again so that they appear "normal"
      doc
        .querySelectorAll('img')
        .forEach((img: any) => img.setAttribute('style', img.getAttribute('style') + ';' + invert));

      if (style.styleSheet) {
        style.styleSheet.cssText = css;
      } else {
        style.append(doc.createTextNode(css));
      }

      appendTo.append(style);
    }

    // Make sure links don't open in iframe
    addTargetBlankToLinks(doc);

    doc.close();

    frame.style.width = '100%';
    frame.style.height = `${get(frame, 'contentWindow.document.body.scrollHeight', 100)}px`;
    frame.contentWindow.scrollTo(0, 0);
  };

  return (
    <iframe
      aria-label={t('email_content', { defaultValue: 'Email content' })}
      data-testid="EmailContentIframe"
      src="about:blank"
      scrolling="yes"
      frameBorder="0"
      ref={writeHTML}
      className="emailContentWrapper"
    />
  );
};

/**
 * Memoized version of ContentIframe component.
 * Prevents loosing scroll state, if read state of the currently
 * selected email changes.
 */
export const EmailContentIframeMemoized = React.memo(EmailContentIframe);

export default EmailContentIframe;
